import DOMPurify from 'dompurify';

// XSS Protection - Sanitize user input
export const sanitizeInput = (input: string): string => {
  if (typeof input !== 'string') return '';
  return DOMPurify.sanitize(input, { 
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: []
  });
};

// Email validation with security considerations
export const validateEmail = (email: string): { isValid: boolean; error?: string } => {
  const sanitized = sanitizeInput(email);
  
  if (!sanitized) {
    return { isValid: false, error: 'E-postadress krävs' };
  }

  // Basic email regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(sanitized)) {
    return { isValid: false, error: 'Ogiltig e-postadress' };
  }

  // Length check
  if (sanitized.length > 254) {
    return { isValid: false, error: 'E-postadress för lång' };
  }

  // Check for dangerous patterns
  const dangerousPatterns = [
    /<script/i,
    /javascript:/i,
    /on\w+=/i,
    /data:/i
  ];

  for (const pattern of dangerousPatterns) {
    if (pattern.test(sanitized)) {
      return { isValid: false, error: 'Ogiltig e-postadress' };
    }
  }

  return { isValid: true };
};

// Name validation
export const validateName = (name: string, fieldName: string = 'Namn'): { isValid: boolean; error?: string } => {
  const sanitized = sanitizeInput(name);
  
  if (!sanitized) {
    return { isValid: false, error: `${fieldName} krävs` };
  }

  if (sanitized.length < 2) {
    return { isValid: false, error: `${fieldName} måste vara minst 2 tecken` };
  }

  if (sanitized.length > 100) {
    return { isValid: false, error: `${fieldName} får vara max 100 tecken` };
  }

  // Only allow letters, spaces, hyphens, and common accented characters
  const nameRegex = /^[a-zA-ZàáâäãåčćđèéêëìíîïñòóôöõøùúûüýÿšžÀÁÂÄÃÅČĆĐÈÉÊËÌÍÎÏÑÒÓÔÖÕØÙÚÛÜÝŸŠŽ\s\-']+$/;
  if (!nameRegex.test(sanitized)) {
    return { isValid: false, error: `${fieldName} innehåller ogiltiga tecken` };
  }

  return { isValid: true };
};

// Phone validation
export const validatePhone = (phone: string): { isValid: boolean; error?: string } => {
  if (!phone) return { isValid: true }; // Phone is optional

  const sanitized = sanitizeInput(phone);
  
  // Allow international formats
  const phoneRegex = /^\+?[\d\s\-\(\)]{6,20}$/;
  if (!phoneRegex.test(sanitized)) {
    return { isValid: false, error: 'Ogiltigt telefonnummer' };
  }

  return { isValid: true };
};

// Company name validation
export const validateCompany = (company: string): { isValid: boolean; error?: string } => {
  if (!company) return { isValid: true }; // Company is optional

  const sanitized = sanitizeInput(company);
  
  if (sanitized.length > 200) {
    return { isValid: false, error: 'Företagsnamn får vara max 200 tecken' };
  }

  return { isValid: true };
};

// General text field validation
export const validateTextField = (
  text: string, 
  fieldName: string, 
  required: boolean = false,
  maxLength: number = 1000
): { isValid: boolean; error?: string } => {
  const sanitized = sanitizeInput(text);
  
  if (required && !sanitized) {
    return { isValid: false, error: `${fieldName} krävs` };
  }

  if (sanitized.length > maxLength) {
    return { isValid: false, error: `${fieldName} får vara max ${maxLength} tecken` };
  }

  return { isValid: true };
};

// Rate limiting helper (client-side)
export class ClientRateLimit {
  private attempts: Map<string, number[]> = new Map();
  
  constructor(private maxAttempts: number = 5, private windowMs: number = 60000) {}
  
  isAllowed(key: string): boolean {
    const now = Date.now();
    const attempts = this.attempts.get(key) || [];
    
    // Remove old attempts outside the window
    const validAttempts = attempts.filter(time => now - time < this.windowMs);
    
    if (validAttempts.length >= this.maxAttempts) {
      return false;
    }
    
    // Add current attempt
    validAttempts.push(now);
    this.attempts.set(key, validAttempts);
    
    return true;
  }
  
  getRemainingTime(key: string): number {
    const attempts = this.attempts.get(key) || [];
    if (attempts.length < this.maxAttempts) return 0;
    
    const oldestAttempt = Math.min(...attempts);
    const remainingTime = this.windowMs - (Date.now() - oldestAttempt);
    
    return Math.max(0, remainingTime);
  }
}

// Create rate limiter instances
export const authRateLimit = new ClientRateLimit(3, 60000); // 3 attempts per minute
export const passwordResetRateLimit = new ClientRateLimit(2, 300000); // 2 attempts per 5 minutes